import LogRocket from 'logrocket'
import ifvisible from 'ifvisible'
import { serverEnvironment } from 'env'
import { Employee } from 'redux/authUser/types'
import { TOrganization } from 'redux/organizationOnborading/organization/reducer'

const blackList = [
  'alraedahfinance',
  'chargebee_stc',
  'irsaa',
  'rega',
  'pca',
  'tcs',
  'spsc',
  'ncpp',
  'mcicall',
  'mohvro',
  'jisr',
  'maalem',
  'mediagov',
  'ahad',
  'clsr',
  'gac',
  'saso',
  'mofugrp',
]

const DELAY = 5000

type Request = {
  body: unknown
  headers: string
  url: string
}

const options = {
  release: import.meta.env.REACT_APP_GIT_TAG || '0.0.0',
  network: {
    requestSanitizer: (request: Request): Request | null => {
      // if the url contains 'delete'
      if (request.url.toLowerCase().indexOf('delete') !== -1) {
        // scrub out the body
        request.body = null
      }

      // if the url contains 'ignore'
      if (request.url.toLowerCase().indexOf('ignore') !== -1) {
        // ignore the request response pair
        return null
      }

      if (request.headers['x-auth-token']) {
        request.headers['x-auth-token'] = ''
      }

      // scrub header pair from request
      request.headers['x-secret'] = null

      // make sure you return the modified request
      return request
    },
  },
  browser: {
    urlSanitizer: (url: string): string => {
      let sanitizedUrl = url

      // redact the path following /ssn/ from the URL
      sanitizedUrl = sanitizedUrl.replace(/\/ssn\/([^/]*)/i, '/ssn/**redacted**')

      // redact the value of the query parameter secret_key
      sanitizedUrl = sanitizedUrl.replace(/secret_key=([^&]*)/, 'secret_key=**redacted**')

      // make sure you return the sanitized URL string
      return sanitizedUrl
    },
  },
}

let isLogRocketInitialized = false

const removeIfVisibleListeners = (): void => {
  ifvisible.off('focus')
  ifvisible.off('blur')
}
const init = (): void => {
  if (!document.hidden) {
    isLogRocketInitialized = true
    // @ts-expect-error Need to check and fix data type
    LogRocket.init('oefpdx/jisr-hr', options)
    removeIfVisibleListeners()
  }
}

const initLogRocket = (): void => {
  setTimeout(() => {
    let id: ReturnType<typeof setTimeout>
    init()
    ifvisible.on('focus', () => {
      if (isLogRocketInitialized) {
        removeIfVisibleListeners()
        return
      }
      id = setTimeout(() => {
        init()
      }, DELAY)
    })
    ifvisible.on('blur', () => {
      clearTimeout(id)
    })
  }, DELAY)
}

const setupLogRocketIdentity = (
  employee: Employee,
  organization: TOrganization['organization'],
): void => {
  const userProps = {
    name: employee?.name_i18n,
    company_name: organization.name,
    slug: employee.organization?.name,
    slug_id: employee.organization?.id,
    employee_id: employee.code,
    id: employee.id,
  }
  LogRocket.identify(userProps?.id.toString(), userProps)
}

const setupLogRocketOnboarding = (org: TOrganization['organization']): void => {
  LogRocket.init('oefpdx/onboarding-project-fzpuw', {
    release: options.release,
  })
  LogRocket.identify(org.id.toString(), {
    name: org.name,
    id: org.id,
    account_type: org.account_type,
    organization_type: org.organization_type || 0,
    micro: org.require_pincode,
  })
}

export const setupLogRocket = (
  employee: Employee | null,
  organization: TOrganization['organization'],
  isInOnboarding: boolean,
): void => {
  if (['aws', 'oci'].includes(serverEnvironment) && isInOnboarding) {
    setupLogRocketOnboarding(organization)
    return
  }

  if (!employee || !employee?.organization || !employee?.organization?.name) return

  const isAWS = serverEnvironment === 'aws'
  const isBlackListed = blackList.includes(employee?.organization?.name.toLowerCase())
  const isAdmin = employee.is_super_admin
  const isLogRocketEnabled = isAWS && !isBlackListed && isAdmin

  if (!isLogRocketEnabled) return

  initLogRocket()
  setupLogRocketIdentity(employee, organization)
}
